import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Box, Typography, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormattedMessage } from "react-intl";
import imageCompression from "browser-image-compression";
import { Photo } from "../../types/rental-type";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";

interface PhotoHandlerProps {
  onPhotosChange: (photos: Photo[]) => void;
  numberOfPhotos: number;
  initialPhotos: Photo[];
  source: string;
}

const PhotoHandler: React.FC<PhotoHandlerProps> = ({
  numberOfPhotos,
  initialPhotos,
  onPhotosChange,
  source,
}) => {
  const fileInputRef = useRef(null);
  const [photos, setPhotos] = useState(initialPhotos);

  const handlePhotosUpload = useCallback(
    async (files: File[]) => {
      try {
        const compressedFiles = await Promise.all(
          files.map((file) => compressImage(file))
        );
        const base64Photos = await Promise.all(
          compressedFiles.map((file) => convertToBase64(file))
        );

        const newPhotos = base64Photos.map((data, index) => ({
          id: uuidv4(),
          source,
          data,
        }));

        setPhotos((prevPhotos) => {
          const updatedPhotos = [...prevPhotos, ...newPhotos];
          onPhotosChange(updatedPhotos);
          return updatedPhotos;
        });
      } catch (error) {
        console.error("Error uploading photos:", error);
        toast.error("Error uploading photos", {
          position: "bottom-right",
        });
      }
    },
    [onPhotosChange, source]
  );

  useEffect(() => {
    const target = document.getElementById("upload-photos");
    const handleFileDrop = (event: any) => {
       // Prevent default browser handling
       event.preventDefault();
       if (event.detail?.dataTransfer?.files) {
          handlePhotosUpload(Array.from(event.detail.dataTransfer.files));
       }
    };
 
    if (target) {
      target.addEventListener("file-drop", handleFileDrop);
  }
 
    return () => {
       if (target) {
          target.removeEventListener("file-drop", handleFileDrop);
       }
    };
 }, [handlePhotosUpload]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handlePhotosUpload(acceptedFiles);
    },
    [handlePhotosUpload]
  );

  const handleRemovePhoto = (index: number) => {
    setPhotos((prevPhotos) => {
      const updatedPhotos = prevPhotos.filter((_, i) => i !== index);
      onPhotosChange(updatedPhotos);
      return updatedPhotos;
    });
  };

  const compressImage = async (file: File): Promise<File> => {
    const options = {
      maxSizeMB: 3,
      maxWidthOrHeight: 1024,
      useWebWorker: true,
    };
    try {
      return await imageCompression(file, options);
    } catch (error) {
      console.error("Error compressing image:", error);
      toast.error("Error compressing image", {
        position: "bottom-right",
      });
      return file; // Return original file if compression fails
    }
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, noClick: false });

  return (
    <Box
      id="upload-photos"
      {...getRootProps()}
      sx={{
        border: "2px dashed gray",
        padding: 2,
        textAlign: "center",
        cursor: "pointer",
        mt: 2,
      }}
      ref={fileInputRef}
    >
      <input {...getInputProps()}  />
      {isDragActive ? (
        <Typography variant="body1">Drop the files here...</Typography>
      ) : (
        <Typography variant="body1" id="drag-and-drop">
          <FormattedMessage id="drag-and-drop" /> (
          {numberOfPhotos > 0 ? numberOfPhotos : 0})
        </Typography>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", mt: 2 }}>
        {photos.map((photo, index) => (
          <Box
            key={index}
            sx={{ position: "relative", width: 100, height: 100, m: 1 }}
          >
            <img
              src={photo.data}
              alt={`${index + 1}`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <IconButton
              onClick={() => handleRemovePhoto(index)}
              sx={{
                position: "absolute",
                top: 1,
                right: 1,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PhotoHandler;
